// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#app-runtime-capacity
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { CircleAlert } from 'lucide-react';

import { Meter } from '@/components/greenlight/Meter';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CLUSTER_SERIES } from '@/components/health/seriesColors';
import { SeriesDot } from '@/components/health/SeriesDot';
import {
  APP_CAPACITY_CRITICAL_UTILIZATION,
  APP_CAPACITY_WARN_UTILIZATION,
  assessAppRuntimeCapacity,
  capacityUtilizationPercent,
  clusterCapacityTotals,
  clusterHeadroom,
  nodeCountsAsRoom,
  type AppRuntimeCapacity,
  type CapacityLevel,
  type ClusterHeadroom,
  type ClusterNode,
  type ClusterTotals,
} from '@greenlight/shared';

type Holder = 'greenlight' | 'apps' | 'kubernetes' | 'unallocatable' | 'free';

const HOLDERS: Record<Holder, { label: string; fill: string }> = {
  greenlight: { label: 'Greenlight control plane', fill: 'hsl(var(--viz-greenlight))' },
  apps: { label: 'Your apps', fill: 'hsl(var(--viz-apps))' },
  kubernetes: { label: 'System pods', fill: 'hsl(var(--viz-system-pods))' },
  unallocatable: { label: 'Not allocatable', fill: 'hsl(var(--viz-unallocatable))' },
  free: { label: 'Free', fill: 'hsl(var(--viz-free))' },
};

/** What the two holders that are not workloads are, beside their legend chips. */
const LEGEND_NOTE: Partial<Record<Holder, string>> = {
  kubernetes: 'Kubernetes and cloud add-ons',
  unallocatable: "kept for the node's own OS",
};

/** Legend order: the subject first, the overhead after it, what is left last. */
const LEGEND: Holder[] = ['greenlight', 'apps', 'kubernetes', 'unallocatable', 'free'];

/** Fill order inside a node: what the node holds back, then what pods reserved, then room. */
const FILL: Holder[] = ['unallocatable', 'kubernetes', 'greenlight', 'apps', 'free'];

const LEVEL_BADGE: Record<CapacityLevel, { variant: StatusBadgeVariant; label: string }> = {
  ok: { variant: 'active', label: 'Healthy' },
  warning: { variant: 'degraded', label: 'warning' },
  critical: { variant: 'failed', label: 'critical' },
  unknown: { variant: 'inactive', label: 'No data' },
};

/** Tiles past this many autoscaler slots fold into one "+N more" tile. */
const MAX_SLOT_TILES = 3;

/**
 * One row of blocks per resource. Sixteen is a 4 vCPU / 16 GiB node at a
 * quarter-vCPU and a GiB per block; larger nodes step the block size up.
 */
const MAX_CELLS = 16;
const CELL = 12;
const GAP = 3;

interface Resource {
  capacity: number;
  allocatable: number;
  requested: { greenlight: number; apps: number; kubernetes: number };
}

interface Unit {
  /** Size of one block, in the resource's own unit (MiB or millicores). */
  size: number;
  /** How one block reads in the legend and the tooltip. */
  describe: string;
}

/** How one resource draws across the cluster. */
interface Scale {
  unit: Unit;
  /** Blocks in the largest node's row, which spans its tile; every other row is drawn to it. */
  cells: number;
}

const cellsFor = (amount: number, unit: Unit): number => Math.max(1, Math.ceil(amount / unit.size));

/** A row's drawn width in its own SVG units. */
const rowWidth = (cells: number): number => cells * CELL + (cells - 1) * GAP;

/**
 * The block size for one resource, fixed across every node so a bigger machine
 * is visibly bigger: the smallest step that keeps the largest node under the
 * block ceiling.
 */
function pickScale(largest: number, steps: readonly Unit[]): Scale {
  const unit = steps.find((u) => cellsFor(largest, u) <= MAX_CELLS) ?? steps[steps.length - 1]!;
  return { unit, cells: cellsFor(largest, unit) };
}

const MEMORY_UNITS: readonly Unit[] = [
  { size: 1024, describe: '1 GiB' },
  { size: 2048, describe: '2 GiB' },
  { size: 4096, describe: '4 GiB' },
  { size: 8192, describe: '8 GiB' },
];
const CPU_UNITS: readonly Unit[] = [
  { size: 250, describe: '¼ vCPU' },
  { size: 500, describe: '½ vCPU' },
  { size: 1000, describe: '1 vCPU' },
  { size: 2000, describe: '2 vCPU' },
];

function amounts(r: Resource): Record<Holder, number> {
  const reserved = r.requested.greenlight + r.requested.apps + r.requested.kubernetes;
  return {
    unallocatable: Math.max(0, r.capacity - r.allocatable),
    kubernetes: r.requested.kubernetes,
    greenlight: r.requested.greenlight,
    apps: r.requested.apps,
    free: Math.max(0, r.allocatable - reserved),
  };
}

/**
 * Which holder each block shows. A block goes to whoever covers most of it, so
 * a sliver of a reservation does not repaint a block that is mostly free; the
 * exact figures are in the tooltip and under the grid.
 */
function blockHolders(r: Resource, unit: Unit): Holder[] {
  const parts = amounts(r);
  const cells = cellsFor(r.capacity, unit);
  const spans: Array<[Holder, number, number]> = [];
  let at = 0;
  for (const holder of FILL) {
    const width = parts[holder] / unit.size;
    spans.push([holder, at, at + width]);
    at += width;
  }
  return Array.from({ length: cells }, (_, i) => {
    let best: Holder = 'free';
    let cover = 0;
    for (const [holder, from, to] of spans) {
      const overlap = Math.max(0, Math.min(i + 1, to) - Math.max(i, from));
      if (overlap > cover) {
        cover = overlap;
        best = holder;
      }
    }
    return best;
  });
}

function percentReserved(r: Resource): number {
  const reserved = r.requested.greenlight + r.requested.apps + r.requested.kubernetes;
  return r.allocatable > 0 ? Math.round((reserved / r.allocatable) * 100) : 0;
}

const gib = (mib: number): string => `${(mib / 1024).toFixed(1)} GiB`;
const vcpu = (m: number): string => `${(m / 1000).toFixed(1)} vCPU`;

/** The diagonal hatch "not allocatable" wears in the grid and in the legend alike. */
function Hatch({ id }: { readonly id: string }): React.ReactElement {
  return (
    <pattern
      id={id}
      width="4"
      height="4"
      patternUnits="userSpaceOnUse"
      patternTransform="rotate(45)"
    >
      <rect width="4" height="4" fill={HOLDERS.unallocatable.fill} />
      <line x1="0" y1="0" x2="0" y2="4" stroke="hsl(var(--slate) / 0.45)" strokeWidth="1.5" />
    </pattern>
  );
}

function Swatch({ holder }: { readonly holder: Holder }): React.ReactElement {
  const hatch = React.useId();
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className="shrink-0">
      {holder === 'unallocatable' ? (
        <defs>
          <Hatch id={hatch} />
        </defs>
      ) : null}
      <rect
        x="0.5"
        y="0.5"
        width="9"
        height="9"
        rx="2"
        fill={holder === 'unallocatable' ? `url(#${hatch})` : HOLDERS[holder].fill}
        stroke={holder === 'free' ? 'hsl(var(--border))' : undefined}
      />
    </svg>
  );
}

function Blocks({
  resource,
  scale,
  format,
  label,
}: {
  readonly resource: Resource;
  readonly scale: Scale;
  readonly format: (value: number) => string;
  readonly label: string;
}): React.ReactElement {
  const hatch = React.useId();
  const { unit } = scale;
  const holders = blockHolders(resource, unit);
  const width = rowWidth(holders.length);
  const height = CELL;
  const parts = amounts(resource);
  const summary = `${label}: ${percentReserved(resource)}% of allocatable reserved — ${LEGEND.filter(
    (h) => h !== 'unallocatable',
  )
    .map((h) => `${HOLDERS[h].label} ${format(parts[h])}`)
    .join(', ')}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="block w-full rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={summary}
        >
          <svg
            viewBox={`0 0 ${width} ${height}`}
            style={{ width: `${(width / rowWidth(scale.cells)) * 100}%` }}
            className="block h-auto"
            aria-hidden="true"
          >
            <defs>
              <Hatch id={hatch} />
            </defs>
            {holders.map((holder, i) => (
              <rect
                key={i}
                x={i * (CELL + GAP)}
                y={0}
                width={CELL}
                height={CELL}
                rx={2}
                fill={holder === 'unallocatable' ? `url(#${hatch})` : HOLDERS[holder].fill}
                stroke={holder === 'free' ? 'hsl(var(--border))' : undefined}
                strokeWidth={holder === 'free' ? 1 : undefined}
              />
            ))}
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent className="text-left">
        <p className="mb-1 font-medium">{label}</p>
        <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 tabular-nums">
          {LEGEND.map((h) => (
            <React.Fragment key={h}>
              <dt>{HOLDERS[h].label}</dt>
              <dd className="text-right">{format(parts[h])}</dd>
            </React.Fragment>
          ))}
        </dl>
        <p className="mt-1">One block is {unit.describe}.</p>
      </TooltipContent>
    </Tooltip>
  );
}

function NodeTile({
  node,
  index,
  memoryScale,
  cpuScale,
  roomiest = false,
}: {
  readonly node: ClusterNode;
  readonly index: number;
  readonly memoryScale: Scale;
  readonly cpuScale: Scale;
  /** The node the next app pod is likeliest to land on: the one with the most room. */
  readonly roomiest?: boolean;
}): React.ReactElement {
  const memory: Resource = {
    capacity: node.memory.capacity_mib,
    allocatable: node.memory.allocatable_mib,
    requested: node.memory.requested_mib,
  };
  const cpu: Resource = {
    capacity: node.cpu.capacity_m,
    allocatable: node.cpu.allocatable_m,
    requested: node.cpu.requested_m,
  };
  const reservedOf = (r: Resource) =>
    r.requested.greenlight + r.requested.apps + r.requested.kubernetes;

  return (
    <div
      className="rounded-[8px] border border-border bg-card p-3"
      data-testid={`node-tile-${index + 1}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-foreground">
          Node {index + 1}
          {roomiest ? (
            <span className="ml-2 text-[11px] font-normal text-muted-foreground">most room</span>
          ) : null}
        </p>
        {!node.ready ? (
          <StatusBadge variant="failed">Not ready</StatusBadge>
        ) : node.schedulable === false ? (
          <StatusBadge variant="degraded">Not taking new pods</StatusBadge>
        ) : null}
      </div>
      <p className="mb-3 text-[11px] text-muted-foreground">
        {Math.round(cpu.capacity / 1000)} vCPU · {Math.round(memory.capacity / 1024)} GiB
      </p>
      {/* One row per resource, read like the cluster bars above it: label and
          figures on top, blocks filling left to right below. */}
      <div className="space-y-3">
        {(
          [
            ['Memory', memory, memoryScale, gib],
            ['CPU', cpu, cpuScale, vcpu],
          ] as const
        ).map(([label, resource, scale, format]) => (
          <div key={label} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2 text-[11px]">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-mono text-muted-foreground">
                {format(reservedOf(resource)).replace(/ (GiB|vCPU)$/, '')} /{' '}
                {format(resource.allocatable)}{' '}
                <span className="text-foreground">{percentReserved(resource)}%</span>
              </span>
            </div>
            <Blocks
              resource={resource}
              scale={scale}
              format={format}
              label={`${label} on node ${index + 1}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

const numberFmt = new Intl.NumberFormat('en-US');

/** One resource summed over the cluster: requested against allocatable, as a meter. */
function TotalBar({
  label,
  color,
  requested,
  allocatable,
  unit,
}: {
  readonly label: string;
  /** Keys this resource's line on the History chart below. */
  readonly color?: string | undefined;
  readonly requested: number;
  readonly allocatable: number;
  readonly unit: string;
}): React.ReactElement {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <SeriesDot color={color} />
          {label}
        </span>
        <span className="font-mono text-foreground">
          {numberFmt.format(requested)} / {numberFmt.format(allocatable)}
          {unit}
          <span className="ml-2 text-muted-foreground">
            {Math.round(capacityUtilizationPercent(requested, allocatable))}%
          </span>
        </span>
      </div>
      <Meter
        value={requested}
        target={allocatable}
        warn={(allocatable * APP_CAPACITY_WARN_UTILIZATION) / 100}
        crit={(allocatable * APP_CAPACITY_CRITICAL_UTILIZATION) / 100}
      />
    </div>
  );
}

const freeCpu = (m: number): string =>
  m >= 1000 ? `${(m / 1000).toFixed(1)} vCPU` : `${numberFmt.format(m)}m CPU`;
const freeMemory = (mib: number): string =>
  mib >= 1024 ? `${(mib / 1024).toFixed(1)} GiB` : `${numberFmt.format(mib)} MiB`;

/**
 * When the next node comes: the autoscaler adds one only when an app pod fits on
 * no running node, so the answer is per node, never a mark on the cluster totals.
 */
function Headroom({
  headroom,
  atMaximum,
}: {
  readonly headroom: ClusterHeadroom;
  /** The app pool is at its maximum, so a pod that does not fit waits instead. */
  readonly atMaximum: boolean;
}): React.ReactElement {
  const { baseline_pods: pods, most_free: free } = headroom;
  const then = atMaximum ? 'waits until the limit is raised' : 'adds a node';
  return (
    <div className="space-y-1 text-xs leading-relaxed" data-testid="cluster-headroom">
      {pods === 0 ? (
        <p className="text-foreground">
          No node has room for another small app, so the next one {then}.
        </p>
      ) : (
        <>
          <p className="text-foreground">
            Room for about {numberFmt.format(pods)} more small {pods === 1 ? 'app' : 'apps'} before{' '}
            {atMaximum ? 'apps start waiting' : 'another node is added'}.
          </p>
          <p className="text-muted-foreground">
            No node has more than {freeCpu(free.cpu_m)} or {freeMemory(free.memory_mib)} free, so an
            app that needs more than that {then}.
          </p>
        </>
      )}
    </div>
  );
}

/** Room the autoscaler has left: a note, not a control, so nothing here looks pressable. */
function SlotTile({ count }: { readonly count: number }): React.ReactElement {
  return (
    <div className="flex min-h-[120px] items-center justify-center rounded-[8px] border border-dashed border-border/80 px-4 text-center">
      <p className="text-[11px] leading-snug text-muted-foreground">
        {count === 1
          ? 'Added automatically if the other nodes fill up'
          : `Up to ${count} more nodes, added automatically if the others fill up`}
      </p>
    </div>
  );
}

/**
 * The cluster's capacity: two bars for the whole cluster at a glance, then each
 * node with what it offers and who has reserved it. Greenlight's pods and your
 * apps share nodes by design (docs/08 §App node scheduling contract), so the
 * card draws the nodes rather than a split the cluster does not have.
 * Requests-based, like the scheduler.
 */
export function ClusterCapacitySection({
  nodes,
  capacity,
  history,
  nodesReported = true,
  reportedTotals,
  nodeCounts,
}: {
  readonly nodes: ClusterNode[] | null | undefined;
  readonly capacity: AppRuntimeCapacity | null;
  /** Totals built from the node list where only they cross, as on a fleet report. */
  readonly reportedTotals?: ClusterTotals;
  /** The card's own history, collapsed below its current readings. */
  readonly history?: React.ReactNode;
  /** False where the source never carries the node list, so its absence is not a pending collection. */
  readonly nodesReported?: boolean;
  /** The control that changes the node counts, under the nodes; absent where they are read-only. */
  readonly nodeCounts?: React.ReactNode;
}): React.ReactElement {
  const assessment = assessAppRuntimeCapacity(capacity, nodes ?? reportedTotals);
  const badge = LEVEL_BADGE[assessment.level];
  const list = nodes ?? [];
  const autoscaledNodes = list.filter((n) => n.pool === 'apps').length;
  const range = capacity?.app_pool_range ?? null;
  const room = range ? Math.max(0, range.max_count - autoscaledNodes) : 0;
  const memoryScale = pickScale(
    Math.max(0, ...list.map((n) => n.memory.capacity_mib)),
    MEMORY_UNITS,
  );
  const cpuScale = pickScale(Math.max(0, ...list.map((n) => n.cpu.capacity_m)), CPU_UNITS);
  const pending = capacity?.pending_unschedulable_count ?? 0;
  const totals = clusterCapacityTotals(nodes ?? reportedTotals, capacity);
  const leftOut = list.filter((n) => !nodeCountsAsRoom(n)).length;
  const headroom = clusterHeadroom(nodes);
  const countedNodes = list.length - leftOut;

  const headline =
    list.length === 0
      ? null
      : `${list.length} node${list.length === 1 ? '' : 's'} · ${
          range
            ? room === 0
              ? 'at its limit'
              : `up to ${room} more added automatically`
            : 'limit not reported'
        }`;

  return (
    <SectionCard
      title="Cluster capacity"
      testId="cluster-capacity"
      className="md:col-span-2"
      badge={
        <div className="flex items-center gap-3">
          {headline ? (
            <span className="hidden text-xs text-muted-foreground sm:inline">{headline}</span>
          ) : null}
          <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
        </div>
      }
    >
      {headline ? <p className="mb-3 text-xs text-muted-foreground sm:hidden">{headline}</p> : null}
      {totals ? (
        <div className="space-y-3">
          <p className="text-xs leading-relaxed text-muted-foreground">
            CPU and memory your pods have reserved, out of what the nodes that are ready now can
            offer.
          </p>
          <TotalBar
            label="CPU requested"
            color={history ? CLUSTER_SERIES.cpu : undefined}
            requested={totals.cpu.requested_m}
            allocatable={totals.cpu.allocatable_m}
            unit="m"
          />
          <TotalBar
            label="Memory requested"
            color={history ? CLUSTER_SERIES.memory : undefined}
            requested={totals.memory.requested_mib}
            allocatable={totals.memory.allocatable_mib}
            unit=" MiB"
          />
          {leftOut > 0 ? (
            <p className="text-xs text-muted-foreground">
              Nodes not ready to take pods are left out of the bars.
            </p>
          ) : null}
          {headroom ? (
            <Headroom headroom={headroom} atMaximum={range !== null && room === 0} />
          ) : null}
        </div>
      ) : list.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          No ready node has reported what it can allocate yet.
        </p>
      ) : null}
      {nodes === undefined ? (
        <p className="mt-3 text-sm text-muted-foreground">
          {!nodesReported
            ? totals
              ? 'Per-node view not reported.'
              : 'Capacity not reported.'
            : totals
              ? 'The per-node view appears with the next health collection.'
              : 'No capacity reading in the latest snapshot.'}
        </p>
      ) : list.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Not measured &mdash; the cluster&rsquo;s node list could not be read.
        </p>
      ) : (
        <TooltipProvider>
          <p className="mb-2 mt-5 text-xs font-medium text-muted-foreground">By node</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((node, i) => (
              <NodeTile
                key={i}
                node={node}
                index={i}
                memoryScale={memoryScale}
                cpuScale={cpuScale}
                roomiest={countedNodes > 1 && headroom?.roomiest_index === i}
              />
            ))}
            {room > 0 && room <= MAX_SLOT_TILES
              ? Array.from({ length: room }, (_, i) => <SlotTile key={`slot-${i}`} count={1} />)
              : null}
            {room > MAX_SLOT_TILES ? <SlotTile count={room} /> : null}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Each block is {memoryScale.unit.describe} of memory or {cpuScale.unit.describe} of CPU,
            colored by who has reserved most of it.
          </p>
          <ul
            className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
            aria-label="Legend"
          >
            {LEGEND.map((h) => (
              <li key={h} className="flex items-center gap-1.5">
                <Swatch holder={h} />
                {HOLDERS[h].label}
                {LEGEND_NOTE[h] ? (
                  <span className="text-muted-foreground">({LEGEND_NOTE[h]})</span>
                ) : null}
              </li>
            ))}
          </ul>
        </TooltipProvider>
      )}
      {nodeCounts}
      {/* Only when something is wrong: app pods the scheduler could not place,
          with its own reason. Greenlight's own pods are not in this count. */}
      {pending > 0 ? (
        <div className="mt-3 border-t border-border pt-3 text-xs">
          <div className="space-y-1">
            <p className="flex items-center gap-1.5 text-foreground">
              <CircleAlert className="size-3.5 text-[hsl(var(--status-red))]" aria-hidden="true" />
              {pending} app pod{pending === 1 ? '' : 's'} cannot be scheduled
            </p>
            {(capacity?.reasons ?? []).map((reason) => (
              <p key={reason} className="break-words font-mono text-[11px] text-muted-foreground">
                {reason}
              </p>
            ))}
          </div>
        </div>
      ) : null}
      {history}
    </SectionCard>
  );
}
